import { postData, sortFunc } from "../../shared/utils";

export function triageSquadViewModal({
  triageSelectionsUrl,
  triageSaveUrl,
  snapshotBaseUrl,
  minResponseThreshold,
}) {
  const { currentUser } = window;

  const triageCommentConfig = {
    noop: {
      label: "Add a comment for context",
      placeholder: "Add any additional context about this driver",
    },
    improvement_area: {
      label: "Add a comment for context",
      placeholder:
        "e.g. 'Planning to introduce planned weekly code review sessions'",
    },
    escalation: {
      label: "What support do you need?",
      placeholder:
        "e.g. 'Need dedicated time allocation for tech debt and buy-in from product'",
    },
  };

  return {
    triageCommentConfig,
    loaded: false,
    snapshotItems: [],
    responseCount: 0,
    minResponseThreshold,
    comments: [],
    commentsOpen: false,
    commentsDriver: null,
    commentsLoaded: false,
    triageComments: {}, // Triage comment text by item ID
    triageCommentOpenIds: {}, // Track which triage comment sections are open
    selections: {},
    submitting: false,
    showIncompleteSaveConfirmation: false,
    reusingPreviousResponses: false,
    compareTo: "vs_prev",
    showOrgComparison: true,
    sortBy: null,
    sortDir: "asc",
    autosaveInProgress: false,
    autosaveStatus: {}, // Track status per item: { [itemId]: 'saving' | 'saved' | 'error' }
    commentDebounceTimers: {}, // Track debounce timers per item
    needsPanelRefresh: false, // Track if any autosave happened (for panel refresh on close)
    init() {
      // Modal might be getting opened directly from triage=1 query param
      if (this.$store.triageSquadManager.squadViewModalOpen) {
        this.fetchData();
      }

      this.$watch("$store.triageSquadManager.squadViewModalOpen", () => {
        if (
          this.$store.triageSquadManager.snapshotSquadId &&
          this.$store.triageSquadManager.squadViewModalOpen
        ) {
          // Reset and fetch data
          this.loaded = false;
          this.snapshotItems = [];
          this.responseCount = 0;
          this.minResponseThreshold = minResponseThreshold;
          this.comments = [];
          this.commentsOpen = false;
          this.commentsDriver = null;
          this.commentsLoaded = false;
          this.triageComments = {};
          this.triageCommentOpenIds = {};
          this.selections = {};
          this.submitting = false;
          this.showIncompleteSaveConfirmation = false;
          this.reusingPreviousResponses = false;
          this.compareTo = "vs_prev";
          this.showOrgComparison = true;
          this.needsPanelRefresh = false;
          this.fetchData();
        }
      });
    },
    fetchData() {
      fetch(`${triageSelectionsUrl}?squad_id=${this.snapshotSquadId}`)
        .then((response) => response.json())
        .then((resp) => {
          resp.forEach((triageResult) => {
            this.selections[triageResult.factor_id] = triageResult.selection;

            // Load existing comment_text and auto-open section if comment exists
            if (triageResult.comment_text) {
              this.triageComments[triageResult.factor_id] =
                triageResult.comment_text;
              this.triageCommentOpenIds[triageResult.factor_id] = true;
            }

            if (!triageResult.submitted_by_user) {
              this.reusingPreviousResponses = true;
            }
          });

          this.fetchSnapshotItems();
        });
    },
    displayableItemCount(item) {
      if (currentUser.is_admin) {
        return item.comment_count - item.comment_count_flagged;
      } else {
        return (
          item.comment_count -
          item.comment_count_admin_only -
          item.comment_count_flagged
        );
      }
    },
    fetchSnapshotItems() {
      fetch(
        `${snapshotBaseUrl}/items?squad_ids=${this.snapshotSquadId}&public_and_private=true&branch=ALL`,
      )
        .then((response) => response.json())
        .then((resp) => {
          const sortedItems = resp.result_items
            .filter((i) => i.current_snapshot)
            .sort(sortFunc("priorityScoreAndCount"));

          const seenNames = [];
          const uniqueItems = [];
          sortedItems.forEach((item) => {
            if (!seenNames.includes(item.name.toLowerCase())) {
              uniqueItems.push(item);
              seenNames.push(item.name.toLowerCase());
            }
          });

          if (typeof resp.benchmark_factors == "object") {
            sortedItems.forEach((row) => {
              const benchmarkFactor = resp.benchmark_factors[row["id"]];

              if (
                benchmarkFactor &&
                row["score"] !== null &&
                row["score"] !== undefined
              ) {
                row["benchmark_50"] = benchmarkFactor["p_50"];
                row["benchmark_75"] = benchmarkFactor["p_75"];
                row["benchmark_90"] = benchmarkFactor["p_90"];

                row["vs_50th"] = row["score"] - benchmarkFactor["p_50"];
                row["vs_75th"] = row["score"] - benchmarkFactor["p_75"];
                row["vs_90th"] = row["score"] - benchmarkFactor["p_90"];
              }
            });
          }

          this.snapshotItems = uniqueItems.slice(0, 16);
          this.responseCount = resp.response_count;
          this.loaded = true;
        });
    },
    makeSelection(itemId, value) {
      this.selections[itemId] = value;
      this.autosaveSelection(itemId, value);
    },
    toggleTriageComment(itemId) {
      // Force reactivity by creating a new object
      this.triageCommentOpenIds = {
        ...this.triageCommentOpenIds,
        [itemId]: !this.triageCommentOpenIds[itemId],
      };
    },
    async autosaveSelection(itemId, selection) {
      if (this.autosaveInProgress) return;

      this.autosaveInProgress = true;
      this.autosaveStatus[itemId] = "saving";

      try {
        await postData(
          `${triageSaveUrl.replace("/save", "/autosave")}?squad_id=${this.snapshotSquadId}`,
          {
            factor_id: itemId,
            selection: selection,
            comment_text: this.triageComments[itemId] || null,
          },
        );

        this.autosaveStatus[itemId] = "saved";
        this.needsPanelRefresh = true;

        // Mark this squad as in-progress by current user in the store
        if (
          !this.$store.triageSquadManager.inProgressSnapshotSquadIdsByCurrentUser.includes(
            this.snapshotSquadId,
          )
        ) {
          this.$store.triageSquadManager.inProgressSnapshotSquadIdsByCurrentUser =
            [
              ...this.$store.triageSquadManager
                .inProgressSnapshotSquadIdsByCurrentUser,
              this.snapshotSquadId,
            ];
        }

        setTimeout(() => {
          this.autosaveStatus[itemId] = null;
        }, 2000);
      } catch (error) {
        console.error("Autosave failed:", error);
        this.autosaveStatus[itemId] = "error";
      } finally {
        this.autosaveInProgress = false;
      }
    },
    autosaveComment(itemId) {
      if (this.commentDebounceTimers[itemId]) {
        clearTimeout(this.commentDebounceTimers[itemId]);
      }

      this.commentDebounceTimers[itemId] = setTimeout(() => {
        this.autosaveSelection(itemId, this.selections[itemId]);
      }, 1000);
    },
    get hasAllSelections() {
      let valid = true;
      Object.values(this.snapshotItems).forEach((item) => {
        if (!this.selections[item.id]) valid = false;
      });
      return valid;
    },
    get isIncomplete() {
      return this.$store.triageSquadManager.currentUserIncompleteSquads[
        this.snapshotSquadId
      ];
    },
    get snapshotSquadId() {
      return this.$store.triageSquadManager.snapshotSquadId;
    },
    get snapshotSquad() {
      return this.$store.triageSquadManager.snapshotSquads[
        this.snapshotSquadId
      ];
    },
    exitSquadViewModal() {
      const shouldReload =
        this.needsPanelRefresh ||
        this.$store.triageSquadManager.needsPanelRefresh;
      this.needsPanelRefresh = false;
      this.$store.triageSquadManager.needsPanelRefresh = false;

      if (this.$store.triageSquadManager.hasMultipleTeams) {
        // Return to multi team modal
        this.$store.triageSquadManager.squadViewModalOpen = false;
        this.$store.triageSquadManager.multiTeamModalOpen = true;
      } else {
        this.$store.triageSquadManager.squadViewModalOpen = false;
      }

      // Reload panel after modal state changes to update in-progress/completed status
      if (shouldReload && typeof window.reloadTriagePanel === "function") {
        // Small delay to let modal close animation complete
        setTimeout(() => window.reloadTriagePanel(), 100);
      }
    },
    save() {
      this.submitting = true;

      // Build triage results array with factor_id, selection, and comment_text
      const triageSelections = this.snapshotItems.map((item) => ({
        factor_id: item.id,
        selection: this.selections[item.id],
        comment_text: this.triageComments[item.id] || null,
      }));

      postData(`${triageSaveUrl}?squad_id=${this.snapshotSquadId}`, {
        triage_selections: triageSelections,
      }).then(() => {
        window.pushNotification({
          title: "Success!",
          desc: "Your responses were saved.",
        });

        if (this.$store?.triageSquadManager) {
          delete this.$store.triageSquadManager.currentUserIncompleteSquads[
            this.snapshotSquadId
          ];
        }

        // Force panel reload to show updated completion status
        this.needsPanelRefresh = true;

        this.exitSquadViewModal();
        this.showIncompleteSaveConfirmation = false;
        this.reusingPreviousResponses = false;

        this.$nextTick(() => {
          this.submitting = false;
        });
      });
    },
    openComments(factorId) {
      this.commentsLoaded = false;
      this.commentsOpen = true;
      this.commentsDriver = this.snapshotItems.find((i) => i.id == factorId);

      fetch(
        `${snapshotBaseUrl}/comments?squad_ids=${this.snapshotSquadId}&factor_id=${factorId}&branch=ALL`,
      )
        .then((response) => response.json())
        .then((resp) => {
          this.comments = resp;
          this.commentsLoaded = true;
        });
    },
    get maxPriority() {
      const arr = this.snapshotItems.map((i) => i.priority);

      return Math.max(...arr);
    },
    get maxDriverScore() {
      const arr = this.snapshotItems.map((i) => i.score);

      return Math.max(...arr);
    },
    get completedCount() {
      return this.snapshotItems.filter((item) => this.selections[item.id])
        .length;
    },
    get totalCount() {
      return this.snapshotItems.length;
    },
    get completionPercentage() {
      if (this.totalCount === 0) return 0;
      return Math.round((this.completedCount / this.totalCount) * 100);
    },
    percentageOfResponses(item, positionCount) {
      if (item.count === 0) return "0%";

      return `${Math.round((100 * item[positionCount]) / item.count)}%`;
    },
    changeComparison(compareTo) {
      this.compareTo = compareTo;
    },
    changeSort(col) {
      if (this.sortBy === col) {
        this.sortDir = this.sortDir === "desc" ? "asc" : "desc";
      } else {
        this.sortBy = col;
        this.sortDir = "asc";
      }
    },
    get sortedSnapshotItems() {
      if (!this.sortBy) return this.snapshotItems;

      const sorted = [...this.snapshotItems];
      sorted.sort((a, b) => {
        let aVal, bVal;
        if (this.sortBy === "score") {
          aVal = a.score;
          bVal = b.score;
        } else if (this.sortBy === "comparison") {
          aVal = this.calculateItemComparison(a);
          bVal = this.calculateItemComparison(b);
        }
        if (aVal == null && bVal == null) return a.name.localeCompare(b.name);
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (aVal === bVal) return a.name.localeCompare(b.name);
        return aVal - bVal;
      });
      return this.sortDir === "desc" ? sorted.reverse() : sorted;
    },
    get comparisonLabel() {
      const labels = {
        vs_prev: "Previous",
        vs_org: "Org average",
        vs_50th: "Industry P50",
        vs_75th: "Industry P75",
        vs_90th: "Industry P90",
      };
      return labels[this.compareTo] || "";
    },
    comparisonTooltip(item) {
      const labels = {
        vs_prev: "Previous",
        vs_org: "Org average",
        vs_50th: "Industry P50",
        vs_75th: "Industry P75",
        vs_90th: "Industry P90",
      };
      const label = labels[this.compareTo];
      if (!label) return "";
      const valueMap = {
        vs_prev: item.previous_score,
        vs_org: item.org_score,
        vs_50th: item.benchmark_50,
        vs_75th: item.benchmark_75,
        vs_90th: item.benchmark_90,
      };
      const value = valueMap[this.compareTo];
      if (value == null) return `Change vs ${label}`;
      return `Change vs ${label} (${Math.round(value)})`;
    },
    calculateItemComparison(item) {
      if (item.score == null) return null;
      if (this.compareTo == "vs_50th") {
        if (item.benchmark_50 == null) return null;
        return item.score - item.benchmark_50;
      } else if (this.compareTo == "vs_75th") {
        if (item.benchmark_75 == null) return null;
        return item.score - item.benchmark_75;
      } else if (this.compareTo == "vs_90th") {
        if (item.benchmark_90 == null) return null;
        return item.score - item.benchmark_90;
      } else if (this.compareTo == "vs_prev") {
        if (item.previous_score == null) return null;
        return item.score - item.previous_score;
      } else if (this.compareTo == "vs_org") {
        if (item.org_score == null) return null;
        return item.score - item.org_score;
      }
    },
  };
}
